import { Award, BookOpen, Heart, Star, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_f651e4ab-7ee9-48a6-ba28-4c2cc6db2347/artifacts/s5qigpsf_Lacresha%20Ramos%20.jpg";

const credentials = [
  "Licensed Massage Therapist (LMT)",
  "16 Years Professional Experience",
  "Certified in Multiple Massage Modalities",
  "Business Owner - 2 Years",
  "Hundreds of Satisfied Clients",
  "Continuing Education Commitment"
];

const timeline = [
  { year: "2009", title: "Journey Begins", description: "Started career as a licensed massage therapist, developing foundational skills in therapeutic techniques." },
  { year: "2015", title: "Advanced Certifications", description: "Obtained advanced certifications in deep tissue, sports massage, and prenatal care." },
  { year: "2020", title: "Client Growth", description: "Built a loyal client base through exceptional service and measurable results." },
  { year: "2023", title: "Millennium Wellness Spa", description: "Founded Millennium Wellness Spa in Daytona Beach to expand reach and impact." },
];

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                Meet the Founder
              </p>
              <h1 className="section-heading text-5xl md:text-6xl font-bold mb-6">
                Lacresha Ramos, LMT
              </h1>
              <p className="section-subheading mb-6">
                With 16 years of dedicated experience in massage therapy, Lacresha Ramos has 
                built a reputation for excellence in therapeutic care. Her hands-on expertise 
                and commitment to client wellness form the foundation of Millennium Wellness Spa.
              </p>
              <p className="section-subheading mb-8">
                Lacresha's approach combines traditional massage techniques with modern 
                understanding of musculoskeletal health, delivering results that keep 
                clients returning year after year.
              </p>
              
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#0F172A]">16+</p>
                  <p className="text-sm text-slate-500">Years Experience</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#0F172A]">500+</p>
                  <p className="text-sm text-slate-500">Happy Clients</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#0F172A]">2</p>
                  <p className="text-sm text-slate-500">Years in Business</p>
                </div>
              </div>
            </div>

            <div className="relative flex justify-center">
              <img 
                src={TEAM_IMAGE}
                alt="Lacresha Ramos, LMT"
                className="w-auto h-[450px] object-cover object-top"
              />
              <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#0F172A] px-4 py-2">
                <p className="text-sm font-semibold">Licensed Massage Therapist</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-white" data-testid="credentials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                Qualifications
              </p>
              <h2 className="section-heading text-4xl font-semibold mb-6">
                Professional Credentials
              </h2>
              <p className="section-subheading mb-8">
                Lacresha's extensive training and certifications ensure clients receive 
                the highest standard of therapeutic care.
              </p>
              
              <div className="space-y-4">
                {credentials.map((credential, index) => (
                  <div 
                    key={credential} 
                    className="flex items-center gap-3 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                    data-testid={`credential-${index}`}
                  >
                    <CheckCircle size={20} className="text-[#D4AF37] flex-shrink-0" />
                    <span className="text-slate-700">{credential}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="feature-card text-center">
                <Award size={32} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0F172A] mb-2">Licensed Professional</h3>
                <p className="text-sm text-slate-600">State certified massage therapist</p>
              </div>
              <div className="feature-card text-center">
                <BookOpen size={32} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0F172A] mb-2">Continuous Learning</h3>
                <p className="text-sm text-slate-600">Ongoing education & certifications</p>
              </div>
              <div className="feature-card text-center">
                <Heart size={32} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0F172A] mb-2">Client Focused</h3>
                <p className="text-sm text-slate-600">Personalized treatment plans</p>
              </div>
              <div className="feature-card text-center">
                <Star size={32} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-semibold text-[#0F172A] mb-2">Proven Results</h3>
                <p className="text-sm text-slate-600">Years of client testimonials</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-50" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Our Journey
            </p>
            <h2 className="section-heading text-4xl font-semibold mb-6">
              Building Excellence Over Time
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-slate-200 hidden md:block" />
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div 
                  key={item.year}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-testid={`timeline-item-${index}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white p-6 border border-slate-100 inline-block">
                      <p className="text-[#D4AF37] font-bold text-2xl mb-2">{item.year}</p>
                      <h3 className="text-xl font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                  
                  <div className="w-4 h-4 bg-[#D4AF37] rounded-full border-4 border-white shadow-lg z-10" />
                  
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
